import React from 'react';
import {
  AlignJustify,
  Box,
  Braces,
  Code,
  Cog,
  Compass,
  FileJson,
  Layers,
  Play,
  Rocket,
  Sparkles,
  Waves,
  X,
  // Database,
} from 'lucide-react';
import { resolveDiscoveryAccess } from '../../config/discoveryActivation';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  // M-D — when false, hide advanced surfaces (AI Optimize, Portfolios,
  // Replay, Deploy, Signals) so the first-run user isn't staring at
  // seven empty pages they don't have context for. Becomes true once
  // they finish or skip the tour; the OnboardingFlow completion
  // callback flips the bit in App.tsx, which re-renders here.
  onboardingCompleted?: boolean;
  // M-F — mobile drawer mode. When `mobileOpen` is non-null, the same
  // Sidebar renders as a slide-in drawer overlaying the page. App.tsx
  // owns the open state; tapping a tab calls onMobileClose so the
  // drawer dismisses automatically.
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface SidebarTab {
  id: string;
  name: string;
  icon: React.ReactNode;
  // Hidden until onboardingCompleted is true. The two always-on tabs
  // (Strategy Builder + Backtest) and Settings stay visible from day 1.
  advanced?: boolean;
  // Hidden unless the named feature flag is enabled (e.g. 'discovery').
  flagged?: 'discovery';
}

const tabs: SidebarTab[] = [
  { id: 'builder',    name: 'Strategy Builder',   icon: <Box className="w-5 h-5" /> },
  { id: 'backtest',   name: 'Backtest & Validate', icon: <AlignJustify className="w-5 h-5" /> },
  { id: 'optimize',   name: 'AI Optimize',         icon: <Sparkles className="w-5 h-5" />, advanced: true },
  { id: 'portfolios', name: 'Portfolios',          icon: <Layers className="w-5 h-5" />,   advanced: true },
  // { id: 'data', name: 'Market Data', icon: <Database className="w-5 h-5" /> },
  { id: 'replay',     name: 'Replay',              icon: <Play className="w-5 h-5" />,     advanced: true },
  { id: 'deploy',     name: 'Deploy',              icon: <Rocket className="w-5 h-5" />,   advanced: true },
  { id: 'signals',    name: 'Signals',             icon: <Waves className="w-5 h-5" />,    advanced: true },
  { id: 'convert',    name: 'Convert',             icon: <Code className="w-5 h-5" />,     advanced: true },
  // Discovery (Phase 3A) — only shown when the Discovery flag is on; filtered in
  // visibleTabs below so a disabled user never sees or reaches it.
  { id: 'discovery',  name: 'Discovery',           icon: <Compass className="w-5 h-5" />,  advanced: true, flagged: 'discovery' },
  { id: 'settings',   name: 'Settings',            icon: <Cog className="w-5 h-5" /> },
];

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  onboardingCompleted = true,
  mobileOpen,
  onMobileClose,
}) => {
  const flagOk = (t: SidebarTab) => t.flagged !== 'discovery' || resolveDiscoveryAccess().allowed;
  const visibleTabs = (onboardingCompleted ? tabs : tabs.filter(t => !t.advanced)).filter(flagOk);
  // M-F — when mobileOpen is defined (i.e. truthy or false), this Sidebar
  // is the MOBILE drawer instance. Render with fixed positioning + a
  // backdrop and animate via translate. When undefined, render as the
  // normal desktop sidebar.
  const isMobile = mobileOpen !== undefined;

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    if (isMobile && onMobileClose) onMobileClose();
  };

  const panel = (
    <div className="w-64 bg-gray-900 text-white h-full flex flex-col">
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-teal-400">Builder Panel</h2>
          {isMobile && onMobileClose ? (
            <button
              onClick={onMobileClose}
              className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          ) : (
            <FileJson className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <ul>
          {visibleTabs.map((tab) => (
            <li key={tab.id} className="mb-1 px-2">
              <button
                onClick={() => handleTabClick(tab.id)}
                className={`w-full flex items-center px-4 py-3 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span className="mr-3">{tab.icon}</span>
                <span>{tab.name}</span>
                {activeTab === tab.id && (
                  <span className="ml-auto w-1.5 h-5 bg-teal-400 rounded-full"></span>
                )}
              </button>
            </li>
          ))}
        </ul>
        {!onboardingCompleted && (
          <div className="mx-3 mt-3 p-3 rounded-md bg-slate-800 border border-slate-700 text-[11px] text-slate-400">
            <strong className="text-slate-200">More unlocks soon.</strong>{' '}
            AI Optimize, Portfolios, Replay, Deploy &amp; Signals appear once you complete the tour.
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-gray-800">
        <div className="bg-gray-800 p-3 rounded-md text-sm">
          <div className="flex items-center mb-2">
            <Braces className="h-4 w-4 text-yellow-500 mr-2" />
            <span className="text-yellow-500 font-medium">Pro Tip</span>
          </div>
          <p className="text-gray-400">
            Use the Block Builder to create strategies that can be backtested
            against market data.
          </p>
        </div>
      </div>
    </div>
  );

  // M-F — desktop branch: static 64-unit-wide column anchored to the side.
  if (!isMobile) {
    return <div className="h-[calc(100vh-64px)]">{panel}</div>;
  }

  // M-F — mobile branch: fixed full-screen drawer with backdrop. Translate
  // off-screen when closed so the slide animation feels native.
  return (
    <>
      {/* Backdrop. Click to dismiss. */}
      <div
        className={`fixed inset-0 z-40 bg-slate-900/70 transition-opacity ${
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onMobileClose}
        aria-hidden="true"
      />
      {/* Drawer. */}
      <div
        className={`fixed top-0 left-0 z-50 h-screen transition-transform duration-200 ease-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        {panel}
      </div>
    </>
  );
};

export default Sidebar;
